import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import AppleIcon from "@mui/icons-material/Apple";
import AndroidIcon from "@mui/icons-material/Android";

import AccessController from "_shared/AccessController";
import { DashGridL2 } from "layouts/dash-grid";
import TopBar from "_shared/Top";
import { Toaster } from "react-hot-toast";
import { toastMessage } from "utils/toastMessage";
import { PageHeader, SectionCard, EmptyState } from "_shared/ui";
import { getMaintenance, addMaintenance } from "_controllers/utilities";

// Format an ISO/date string for a <input type="datetime-local"> value.
const toLocalInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  // Adjust to local timezone for the input, then trim seconds.
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
};

const Main = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [current, setCurrent] = useState(null);

  const [form, setForm] = useState({
    maintenanceMode: false,
    iOSmaintenanceMode: false,
    androidMaintenanceMode: false,
    endDate: "",
    maintenanceReason: "",
  });
  const [errors, setErrors] = useState({});

  const loadCurrent = async () => {
    try {
      setLoading(true);
      const res = await getMaintenance();
      const record = res?.maintainence?.[0] || null;
      setCurrent(record);
      if (record) {
        setForm({
          maintenanceMode: !!record.maintenanceMode,
          iOSmaintenanceMode: !!record.iOSmaintenanceMode,
          androidMaintenanceMode: !!record.androidMaintenanceMode,
          endDate: toLocalInput(record.endDate),
          maintenanceReason: record.maintenanceReason || "",
        });
      }
    } catch (err) {
      toastMessage(
        err?.response?.data?.error || "Failed to load maintenance status",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCurrent();
  }, []);

  const setField = (name) => (value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.endDate) {
      next.endDate = "End date & time is required";
    } else {
      const end = new Date(form.endDate);
      if (Number.isNaN(end.getTime())) next.endDate = "Invalid date";
      else if (end.getTime() <= Date.now())
        next.endDate = "End date must be in the future";
    }
    // Reason is required whenever maintenance is being enabled anywhere.
    const anyEnabled =
      form.maintenanceMode ||
      form.iOSmaintenanceMode ||
      form.androidMaintenanceMode;
    if (anyEnabled && !form.maintenanceReason.trim()) {
      next.maintenanceReason =
        "Please provide a reason when enabling maintenance";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmitClick = () => {
    if (!validate()) return;
    // Enabling global maintenance blocks the app for all users — confirm first.
    if (form.maintenanceMode && !current?.maintenanceMode) {
      setConfirmOpen(true);
      return;
    }
    submit();
  };

  const submit = async () => {
    setConfirmOpen(false);
    try {
      setSubmitting(true);
      const payload = {
        maintenanceMode: form.maintenanceMode,
        iOSmaintenanceMode: form.iOSmaintenanceMode,
        androidMaintenanceMode: form.androidMaintenanceMode,
        endDate: new Date(form.endDate).toISOString(),
        maintenanceReason: form.maintenanceReason.trim(),
      };
      await addMaintenance(payload);
      toastMessage("Maintenance settings saved", "success");
      await loadCurrent();
    } catch (err) {
      toastMessage(
        err?.response?.data?.error || "Failed to save maintenance settings",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // One-click revert: post a record with every mode off and endDate = now, so
  // the newest record turns the apps back to live.
  const endMaintenance = async () => {
    setEndOpen(false);
    try {
      setSubmitting(true);
      await addMaintenance({
        maintenanceMode: false,
        iOSmaintenanceMode: false,
        androidMaintenanceMode: false,
        endDate: new Date().toISOString(),
        maintenanceReason: "Maintenance ended",
      });
      toastMessage("Maintenance ended — apps are live", "success");
      await loadCurrent();
    } catch (err) {
      toastMessage(
        err?.response?.data?.error || "Failed to end maintenance",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const maintenanceActive =
    current &&
    (current.maintenanceMode ||
      current.iOSmaintenanceMode ||
      current.androidMaintenanceMode);

  const platformSwitches = [
    { key: "iOSmaintenanceMode", label: "iOS", icon: <AppleIcon /> },
    { key: "androidMaintenanceMode", label: "Android", icon: <AndroidIcon /> },
  ];

  return (
    <>
      <PageHeader
        title="App Maintenance"
        subtitle="Put the mobile apps into maintenance mode and schedule a window"
      />

      {/* Current status */}
      <SectionCard
        title="Current Status"
        sx={{ mb: 3 }}
        action={
          maintenanceActive ? (
            <Button
              size="small"
              variant="outlined"
              color="success"
              onClick={() => setEndOpen(true)}
              disabled={submitting}
            >
              End maintenance now
            </Button>
          ) : null
        }
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : current ? (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" color="text.secondary">
                Overall:
              </Typography>
              <Chip
                label={current.maintenanceMode ? "Maintenance ON" : "Live"}
                color={current.maintenanceMode ? "warning" : "success"}
                size="small"
              />
              <Chip
                icon={<AppleIcon />}
                label={current.iOSmaintenanceMode ? "iOS: ON" : "iOS: Live"}
                color={current.iOSmaintenanceMode ? "warning" : "default"}
                variant={current.iOSmaintenanceMode ? "filled" : "outlined"}
                size="small"
              />
              <Chip
                icon={<AndroidIcon />}
                label={
                  current.androidMaintenanceMode ? "Android: ON" : "Android: Live"
                }
                color={current.androidMaintenanceMode ? "warning" : "default"}
                variant={current.androidMaintenanceMode ? "filled" : "outlined"}
                size="small"
              />
            </Stack>
            <Divider />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 4 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Window ends
                </Typography>
                <Typography variant="body2">
                  {current.endDate
                    ? new Date(current.endDate).toLocaleString()
                    : "—"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Reason
                </Typography>
                <Typography variant="body2">
                  {current.maintenanceReason || "—"}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        ) : (
          <EmptyState
            icon={BuildCircleOutlinedIcon}
            title="No maintenance configured"
            description="The apps are live. Use the form below to schedule a maintenance window."
          />
        )}
      </SectionCard>

      {/* Configure */}
      <SectionCard title="Configure Maintenance">
        <Stack spacing={3}>
          <FormControlLabel
            control={
              <Switch
                checked={form.maintenanceMode}
                onChange={(e) => setField("maintenanceMode")(e.target.checked)}
                color="warning"
              />
            }
            label={
              <Box>
                <Typography sx={{ fontWeight: 600 }}>
                  Global maintenance mode
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Blocks access across all platforms while enabled.
                </Typography>
              </Box>
            }
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Per-platform overrides
            </Typography>
            <Grid container spacing={1}>
              {platformSwitches.map((p) => (
                <Grid item xs={12} sm={6} key={p.key}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form[p.key]}
                        onChange={(e) => setField(p.key)(e.target.checked)}
                        color="warning"
                      />
                    }
                    label={
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {p.icon}
                        <span>{p.label}</span>
                      </Stack>
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          <TextField
            type="datetime-local"
            label="Maintenance ends at"
            value={form.endDate}
            onChange={(e) => setField("endDate")(e.target.value)}
            error={!!errors.endDate}
            helperText={errors.endDate}
            InputLabelProps={{ shrink: true }}
            sx={{ maxWidth: { sm: 320 } }}
            required
          />

          <TextField
            label="Reason"
            placeholder="e.g. Scheduled server upgrade"
            value={form.maintenanceReason}
            onChange={(e) => setField("maintenanceReason")(e.target.value)}
            error={!!errors.maintenanceReason}
            helperText={errors.maintenanceReason}
            multiline
            minRows={2}
            fullWidth
          />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="inherit"
              onClick={loadCurrent}
              disabled={submitting}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleSubmitClick}
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <BuildCircleOutlinedIcon />
                )
              }
            >
              Save Settings
            </Button>
          </Stack>
        </Stack>
      </SectionCard>

      {/* Confirm enabling global maintenance */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Enable global maintenance?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will block access to the app for all users until{" "}
            <strong>
              {form.endDate ? new Date(form.endDate).toLocaleString() : "—"}
            </strong>
            . Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="warning" onClick={submit}>
            Enable maintenance
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm ending maintenance (revert) */}
      <Dialog open={endOpen} onClose={() => setEndOpen(false)}>
        <DialogTitle>End maintenance now?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This turns maintenance off for all platforms and makes the apps live
            immediately. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setEndOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={endMaintenance}>
            End maintenance
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const AppMaintenance = () => (
  <AccessController roles={["ADMIN"]}>
    <DashGridL2 Top={TopBar} Main={Main} />
    <Toaster />
  </AccessController>
);

export default AppMaintenance;
